import { useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTrainingPositions } from '@/hooks/useTrainingPositions';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Target, TrendingUp as TrendingUpIcon, Clock, Archive, Play, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface MistakeLibraryProps {
  onStartTraining: (positions: any[]) => void;
}

export function MistakeLibrary({ onStartTraining }: MistakeLibraryProps) {
  const navigate = useNavigate();
  const { positions, isLoading, loadPositions, archivePosition, getStats } = useTrainingPositions();

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  const stats = getStats();

  const filterByCategory = (category: string) => {
    return positions.filter(p => p.position_category === category);
  };

  const filterByMistakeType = (type: string) => {
    return positions.filter(p => p.mistake_type === type);
  };

  const dueForReview = positions.filter(p => 
    new Date(p.next_review_date) <= new Date()
  );

  const PositionCard = ({ position }: { position: any }) => (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="capitalize">
              {position.mistake_type.replace('_', ' ')}
            </Badge>
            <Badge variant="outline" className="capitalize">
              {position.position_category}
            </Badge>
            <Badge variant="secondary">
              {position.difficulty_rating}
            </Badge>
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Target className="w-3 h-3" />
              <span>Mastery: {position.mastery_level}/5</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingUpIcon className="w-3 h-3" />
              <span>{position.times_solved}/{position.times_attempted}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{new Date(position.next_review_date).toLocaleDateString()}</span>
            </div>
          </div>

          <p className="text-sm">
            Best move: <span className="font-mono">{position.correct_move_san}</span>
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            onClick={() => onStartTraining([position])}
          >
            <Play className="w-4 h-4 mr-2" />
            Train
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => archivePosition(position.id)}
          >
            <Archive className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );

  if (isLoading) {
    return <div className="text-center py-8">Loading positions...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Card */}
      <Card className="p-6 border-2 border-primary/20 bg-primary/5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold mb-1">Track Your Progress</h3>
            <p className="text-sm text-muted-foreground">
              View detailed statistics and insights about your training
            </p>
          </div>
          <Button onClick={() => navigate('/training/stats')}>
            <BarChart3 className="w-4 h-4 mr-2" />
            View Stats
          </Button>
        </div>
      </Card>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <h3 className="text-sm font-medium text-muted-foreground">Total Positions</h3>
          <p className="text-2xl font-bold mt-1">{stats.total}</p>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm font-medium text-muted-foreground">Mastered</h3>
          <p className="text-2xl font-bold mt-1 text-green-500">{stats.mastered}</p>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm font-medium text-muted-foreground">Due for Review</h3>
          <p className="text-2xl font-bold mt-1 text-orange-500">{stats.needsReview}</p>
        </Card>
        <Card className="p-4">
          <Button 
            className="w-full h-full"
            disabled={dueForReview.length === 0}
            onClick={() => onStartTraining(dueForReview)}
          >
            <Play className="w-4 h-4 mr-2" />
            Start Review Session
          </Button>
        </Card>
      </div>

      {/* Positions by Type */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="all">All ({positions.length})</TabsTrigger>
          <TabsTrigger value="blunder">Blunders ({stats.byType['blunder'] || 0})</TabsTrigger>
          <TabsTrigger value="mistake">Mistakes ({stats.byType['mistake'] || 0})</TabsTrigger>
          <TabsTrigger value="tactical">Tactical ({filterByCategory('tactical').length})</TabsTrigger>
          <TabsTrigger value="endgame">Endgame ({filterByCategory('endgame').length})</TabsTrigger>
          <TabsTrigger value="opening">Opening ({filterByCategory('opening').length})</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-3 mt-4">
          {positions.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">No training positions yet.</p>
              <p className="text-sm text-muted-foreground mt-2">
                Play some games and analyze them to extract training positions!
              </p>
            </Card>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">All Positions</h3>
                <Button onClick={() => onStartTraining(positions)}>
                  <Play className="w-4 h-4 mr-2" />
                  Train All
                </Button>
              </div>
              {positions.map(position => (
                <PositionCard key={position.id} position={position} />
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="blunder" className="space-y-3 mt-4">
          {filterByMistakeType('blunder').map(position => (
            <PositionCard key={position.id} position={position} />
          ))}
        </TabsContent>

        <TabsContent value="mistake" className="space-y-3 mt-4">
          {filterByMistakeType('mistake').map(position => (
            <PositionCard key={position.id} position={position} />
          ))}
        </TabsContent>

        <TabsContent value="tactical" className="space-y-3 mt-4">
          {filterByCategory('tactical').map(position => (
            <PositionCard key={position.id} position={position} />
          ))}
        </TabsContent>

        <TabsContent value="endgame" className="space-y-3 mt-4">
          {filterByCategory('endgame').map(position => (
            <PositionCard key={position.id} position={position} />
          ))}
        </TabsContent>

        <TabsContent value="opening" className="space-y-3 mt-4">
          {filterByCategory('opening').map(position => (
            <PositionCard key={position.id} position={position} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
